import type {
  HouseAreaEquipment,
  HousePhotoAttributes,
  HousePhotosAttributesFormatted,
} from 'lc-services/types'
import { isBedroomArea } from '~/helpers/house/bedrooms'
import type { HouseAreaFormatted } from '~/types/house/types'

const EMPTY_PHOTO_GALLERY = {
  photos: [],
  totalCount: 0,
  totalPages: 0,
}
export const useHousePhotos = ({
  areaSelected,
  houseAreaEquipments = [],
  houseDetails,
  housePhotos = EMPTY_PHOTO_GALLERY,
}: {
  areaSelected?: Ref<string>
  houseDetails: { id: string; firstPhoto: HousePhotoAttributes }
  housePhotos: HousePhotosAttributesFormatted | null
  houseAreaEquipments?: HouseAreaEquipment[]
}) => {
  const isLoading = ref(false)
  const hasError = ref(false)
  const showScrollToTopButton = ref(false)
  const sliderIsOpen = ref(false)
  const openedIndex = ref(0)
  const containerRef = ref<HTMLDivElement | null>()
  const galleryRefs = ref<HTMLDivElement[]>([])

  const { $lcServicesSearch } = useNuxtApp()
  const { searchController, searchPresenter } = $lcServicesSearch
  const { query } = useRoute()
  const { isMobile } = useBreakpoint()
  const { setLock } = useLockBody(!isMobile.value)

  // Photo gallery of unassigned photos
  const photoGallery = useState<HousePhotosAttributesFormatted>(
    `house-photos-${houseDetails.id}`,
    () => housePhotos ?? searchPresenter.vm.housePhotos ?? EMPTY_PHOTO_GALLERY,
  )
  // Start on the 1st page if there are no photos
  const currentPage = ref(Math.floor(photoGallery.value.photos.length / 8) + 1)

  // Photos by house area
  const photosWithArea = computed<[string, HouseAreaFormatted][]>(() =>
    (houseAreaEquipments ?? [])
      .flatMap((areaGroup) => areaGroup.areas)
      .flatMap((area) => {
        const photos = area.photos?.filter((photo) => !photo.hidden) ?? []
        if (!photos?.length) return []

        const bed = area.equipments?.find((eq) => eq.bedSize) ?? null
        return {
          bed,
          highlights: {
            ...(area?.bathType ? { bathType: area.bathType } : null),
            ...(area?.view ? { view: area.view } : null),
          },
          name: area.name,
          photoGallery: {
            photos,
            totalCount: photos.length,
            totalPages: 1,
          },
          // @ts-expect-error property 'position' does not exist on typeof area
          position: area.position ?? 0,
        }
      })
      .map((area) => [
        hashify(area.name),
        { ...area, name: beautify(area.name) } as const,
      ]),
  )
  const areaPhotosIds = computed(() =>
    photosWithArea.value.flatMap(([_, area]) =>
      area.photoGallery.photos.map((photo) => photo.id.toString()),
    ),
  )
  const hasPhotosByArea = computed(
    () =>
      areaPhotosIds.value.length > 0 &&
      // Do not show photos by area if a bedroom has no photo
      (houseAreaEquipments ?? [])
        .flatMap((item) => item.areas.filter(isBedroomArea))
        .every((bedroom) => bedroom.photos?.length),
  )
  // Rest of the photos (unassigned to a house area)
  const unassignedPhotos = computed<HouseAreaFormatted>(() => {
    const photosIds = [
      houseDetails.firstPhoto.id.toString(),
      ...areaPhotosIds.value,
    ].filter(Boolean)
    const photos = photoGallery.value.photos
      .filter((photo) => !photo.hidden)
      .filter(
        hasPhotosByArea.value
          ? (photo) => !photosIds.includes(photo.id.toString())
          : () => true,
      )
    return {
      bed: null,
      highlights: null,
      name: { en: 'Discover the house', fr: 'Visiter la propriété' },
      photoGallery: {
        ...photoGallery.value,
        photos,
        totalCount: photos.length,
      },
      position: Infinity,
    }
  })
  // All photos indexed by house area (including unassigned photos)
  const allPhotosByArea = computed(() =>
    photosWithArea.value.concat(
      unassignedPhotos.value.photoGallery.photos.length
        ? [['unassigned', unassignedPhotos.value]]
        : [],
    ),
  )
  // All photos as an array (with cover photo as 1st photo)
  const allHousePhotos = computed(() => {
    const allPhotos = allPhotosByArea.value.flatMap(([hash, area]) =>
      area.photoGallery.photos.map((photo) => ({
        ...photo,
        area: area.name,
        hash,
      })),
    )
    const coverPhotoIndex = allPhotos.findIndex(
      (photo) => photo.id.toString() === houseDetails.firstPhoto.id.toString(),
    )
    // The cover photo must have an area + hash, even if not linked to an area
    const coverPhoto = allPhotos[coverPhotoIndex] ?? {
      ...houseDetails.firstPhoto,
      area: { en: 'Discover the house', fr: 'Visiter la propriété' },
      hash: 'unassigned',
    }
    // The cover photo is not in the gallery
    if (coverPhotoIndex < 0) return [coverPhoto, ...allPhotos]
    // The cover photo is not the first in the gallery
    if (coverPhotoIndex > 0)
      return [
        coverPhoto,
        ...allPhotos.filter((photo) => photo.id !== coverPhoto.id),
      ]
    // The cover photo is first in the gallery
    return allPhotos
  })

  // As all photos by areas are already loaded, just check if there are more unassigned photos to load
  const hasLoadedAllPhotos = computed(
    () =>
      // a house has all its photos associated with an area, no more photos to load
      (areaPhotosIds.value.length > 0 && photoGallery.value.totalCount === 0) ||
      // all photos from the unassigned gallery are loaded
      (photoGallery.value.totalCount > 0 &&
        photoGallery.value.photos.length >= photoGallery.value.totalCount),
  )
  // When filtering out areaPhotosIds, the API removes them from the total count
  const totalCount = computed(
    () => photoGallery.value.totalCount + areaPhotosIds.value.length,
  )

  const fetchHousePhotos = async () => {
    if (isLoading.value || hasLoadedAllPhotos.value) return photoGallery.value
    try {
      isLoading.value = true
      await searchController.getHousePhotos(
        houseDetails.id,
        {
          page: Math.max(
            1,
            Math.min(currentPage.value, photoGallery.value.totalPages),
          )?.toString(),
          per: '8',
        },
        query['private-token']?.toString(),
        areaPhotosIds.value.length
          ? {
              fields: {
                id: {
                  operation: 'notEquals',
                  value: areaPhotosIds.value.join(','),
                },
              },
            }
          : undefined,
      )
      const newPhotos = searchPresenter.vm.housePhotos
      if (newPhotos) {
        photoGallery.value = {
          ...newPhotos,
          photos: photoGallery.value.photos
            .concat(
              newPhotos.photos.flatMap((photo) =>
                photoGallery.value.photos.every((p) => p.id !== photo.id)
                  ? [photo]
                  : [],
              ),
            )
            .sort((a, b) => a.position - b.position),
        }
        if (newPhotos.totalCount && currentPage.value < newPhotos.totalPages) {
          currentPage.value++
        }
      }
    } catch {
      hasError.value = true
    } finally {
      isLoading.value = false
    }
    return searchPresenter.vm.housePhotos ?? EMPTY_PHOTO_GALLERY
  }
  const debouncedFetchPhotos = useDebounceFn(fetchHousePhotos, 500)

  const getPhotoIndex = async (photoId: string | number): Promise<number> => {
    const photoIndex = allHousePhotos.value.findIndex(
      (photo) => photo.id.toString() === photoId.toString(),
    )
    if (photoIndex < 0 && !hasLoadedAllPhotos.value) {
      const res = await fetchHousePhotos()
      if (
        res?.totalCount &&
        !hasError.value &&
        currentPage.value <= res.totalPages
      ) {
        return getPhotoIndex(photoId)
      }
    }
    return photoIndex + 1
  }

  const onScroll = async (e: Event) => {
    // on desktop: onScroll is called with the 'section' element
    // on mobile: onScroll is called with the '#document'
    const container =
      (e.target as Document)?.documentElement ?? (e.target as HTMLDivElement)
    showScrollToTopButton.value = Boolean(container?.scrollTop)
    if (!container) return

    if (isRef(areaSelected)) {
      const containerBounds = container.getBoundingClientRect()
      // Assign area to the photo-grid closest to the container scrollTop
      areaSelected.value = galleryRefs.value.reduce((acc, current) => {
        if (typeof acc.getBoundingClientRect !== 'function') return current
        const prevBounds = acc.getBoundingClientRect()
        const currBounds = current.getBoundingClientRect()
        if (
          currBounds.top <= containerBounds.height &&
          Math.abs(currBounds.top - containerBounds.top) <
            Math.abs(prevBounds.top - containerBounds.top)
        ) {
          return current
        }
        return acc
      }, {} as HTMLDivElement)?.id
    }
    if (!hasLoadedAllPhotos.value) {
      const scrollBottom = Math.abs(
        container.scrollHeight - container.clientHeight - container.scrollTop,
      )
      // as a row has a max-height of 296px, load the next batch when it enters the viewport
      if (scrollBottom <= 296) await debouncedFetchPhotos()
    }
  }
  const onScrollEnd = useDebounceFn(onScroll, 200)

  const openSlider = async (photoId: string) => {
    openedIndex.value = await getPhotoIndex(photoId)
    setLock(true)
    sliderIsOpen.value = true
  }
  const closeSlider = () => {
    if (!sliderIsOpen.value) return
    setLock(false)
    sliderIsOpen.value = false
    openedIndex.value = -1
  }

  return {
    allHousePhotos,
    allPhotosByArea,
    closeSlider,
    containerRef,
    galleryRefs,
    hasError,
    hasLoadedAllPhotos,
    hasPhotosByArea,
    fetchHousePhotos,
    getPhotoIndex,
    isLoading,
    onScrollEnd,
    openedIndex,
    openSlider,
    photoGallery,
    showScrollToTopButton,
    sliderIsOpen,
    totalCount,
    unassignedPhotos,
  }
}
